import { useEffect, useMemo } from 'react';
import {
  Alert,
  Form,
  useForm,
  SIZE,
  Stack,
  Text,
  Center,
  Spinner,
} from '@quinyx/ui';
import * as z from 'zod';

import { useEditConfigs, useGetConfigs } from '../../hooks/configs';
import { ConfigType } from '../../api/interfaces/customer';
import { SHOW_CONFIG } from './constants';
import { useTranslate } from '../../hooks/translate';

type ConfigProps = {
  customerId: string;
  isDisabled?: boolean;
};

type FormValues = Record<string, boolean>;

const emptyArr: ConfigType[] = [];

const Config = ({ customerId, isDisabled = false }: ConfigProps) => {
  const { t, translationExists } = useTranslate();
  const { data: configs = emptyArr, isLoading: isLoadingConfigs } =
    useGetConfigs(String(customerId));
  const { updateConfigs, isPending: isUpdating } = useEditConfigs();

  const configsInUse = useMemo(
    () => configs.filter((config) => SHOW_CONFIG.includes(config.identifier)),
    [configs],
  );

  const initialValues = useMemo(
    () =>
      configsInUse.reduce((acc: FormValues, config) => {
        acc[config.identifier] = config.value || false;
        return acc;
      }, {}),
    [configsInUse],
  );

  const schema = useMemo(() => z.record(z.string(), z.boolean()), []);

  const formMethods = useForm({
    defaultValues: initialValues,
    schema,
    mode: 'onChange',
  });

  useEffect(() => {
    formMethods.reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleFormSubmit = (payload: FormValues) => {
    const configPayload = configsInUse.map((config) => ({
      identifier: config.identifier,
      value: payload[config.identifier] ? 'true' : 'false',
    }));

    updateConfigs({ domainId: customerId, payload: configPayload });
  };

  const isLoading = isLoadingConfigs || isUpdating;

  if (isLoadingConfigs) {
    return (
      <Center>
        <Spinner size={SIZE.L} />
      </Center>
    );
  }

  return (
    <Form methods={formMethods} onSubmit={handleFormSubmit}>
      <Stack spacing={SIZE.M}>
        <Alert type="info">
          <Text variant="smallRegular">{t('config.notCopiedFromProd')}</Text>
        </Alert>

        <Stack spacing={SIZE.XS}>
          <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
            {t('config.configs')}
          </Text>
          {configsInUse.map((config) => (
            <Form.Checkbox
              data-test-id={`config-${config.identifier}`}
              key={config.identifier}
              name={config.identifier}
              label={t(`config.${config.identifier}`, {
                defaultValue: config.identifier,
              })}
              info={
                translationExists(`config.${config.identifier}Info`)
                  ? t(`config.${config.identifier}Info`)
                  : undefined
              }
              disabled={isDisabled}
            />
          ))}
        </Stack>

        <Stack alignItems="flex-end">
          <Form.SubmitButton
            text={t('config.updateConfig')}
            isLoading={isLoading}
            disabled={isDisabled}
          />
        </Stack>
      </Stack>
    </Form>
  );
};

export default Config;
